import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Message} from 'primereact/message';
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {getKeyValue, isAdmin, user} from "../../utils/Utils";
import {usage_types} from "../../utils/Constants";
import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {applyCampaignCard, activeCampaignCard, CARD_BUYBOX, CARD_MARKETPLACE_OPERATOR} from "./marketplaceOperatorSettings";

// Icon/color per campaign type card. Description text comes from the
// translation file: wizard.usageDesc.<value>. The synthetic
// 'marketplace_buybox' entry is not a real usage_type value -- it always pins
// usage_type to 'seller' (see selectMarketplaceBuybox below) -- so it is kept
// out of utils/Constants.js's usage_types()/usage_type_meta, which are also
// consumed elsewhere (campaign list badges, topbar selector, admin dropdowns)
// as the real usage_type enum.
const USAGE_TYPE_META = {
    seller:             {icon: 'pi pi-dollar',        color: '#2563eb'},
    manufacturer:       {icon: 'pi pi-shield',        color: '#dc2626'},
    market_search:      {icon: 'pi pi-search',        color: '#16a34a'},
    drop_shipping:      {icon: 'pi pi-shopping-cart', color: '#d97706'},
    marketplace_buybox: {icon: 'pi pi-shopping-bag',  color: '#7c3aed'},
    marketplace_operator: {icon: 'pi pi-building',   color: '#0d9488'},
};

const MARKETPLACE_BUYBOX = 'marketplace_buybox';
// Synthetic card too: it stores usage_type 'seller' + settings.marketplace_operator
// (see applyCampaignCard in marketplaceOperatorSettings.js).
const MARKETPLACE_OPERATOR = 'marketplace_operator';

const CampaignEdit = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const {selectedCampaign} = React.useContext(CampaignContext);

    const mode = props.mode || 'edit';
    const isWizard = mode === 'wizard';

    const [campaign, setCampaign] = useState(selectedCampaign ?? {name:""});
    const [formErrors, setFormErrors] = useState({name:""});

    useEffect(() =>{
        if (campaign && campaign.settings == null) {
            campaign.settings = {usage_type : user().usage_type, variant_import : user().settings?.variant_import ?? false}
            setCampaign({...campaign});
        }
        let parent_campaigns = props.campaigns ? props.campaigns.filter(c => c.id === campaign?.parent_campaign_id) : []
        if (parent_campaigns.length === 1) {
            campaign.parent_campaign = parent_campaigns[0]
            setCampaign({...campaign});
        }
        // Wizard moduna ilk draft'ı bildir.
        if (isWizard && props.onDraftChange) props.onDraftChange(campaign);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = () => {
        props.save(campaign)
    };

    const pushDraft = (next) => {
        setCampaign(next);
        if (isWizard && props.onDraftChange) props.onDraftChange(next);
    };

    const onChange = (e) =>{
        const { key, value } = getKeyValue(e);

        let next;
        if (key.includes('.')){
            let p = {...(campaign[key.split('.')[0]] ?? {})};
            p[key.split('.')[1]] = value;
            next = {...campaign, [key.split('.')[0]] : p};
        }
        else{
            setFormErrors({...formErrors, [key]: checkError(key, value)});
            next = {...campaign, [key] : value};
        }
        pushDraft(next);
    };

    // Usage-type card selection: updates settings.usage_type and clears
    // settings.marketplace_api and settings.marketplace_operator -- picking any of
    // the four real types means the campaign is not (or no longer) a Marketplace
    // Buybox or Marketplace Operator campaign.
    const selectUsageType = (value) => {
        pushDraft({...campaign, settings: applyCampaignCard(campaign.settings, value)});
    };

    // Marketplace Buybox card: pins usage_type to 'seller' (a marketplace API
    // campaign reprices the merchant's own listings, same as Competitive) and
    // marks settings.marketplace_api with an empty object -- the actual
    // marketplace and credentials are collected later, in the wizard's connect
    // step. Presence of settings.marketplace_api (not its contents) is what
    // distinguishes this card from Competitive; see the `active` check below.
    const selectMarketplaceBuybox = () => {
        pushDraft({...campaign, settings: applyCampaignCard(campaign.settings, CARD_BUYBOX)});
    };

    // Marketplace Operator card: pins usage_type to 'seller' and enables
    // settings.marketplace_operator with empty keys, which the operator then fills
    // in on the campaign settings screen. Clears settings.marketplace_api.
    const selectMarketplaceOperator = () => {
        pushDraft({...campaign, settings: applyCampaignCard(campaign.settings, CARD_MARKETPLACE_OPERATOR)});
    };

    const errorClass = (key) => {
        return formErrors[key] && formErrors[key].length > 0 ? "p-error" : null
    };

    const checkError = (key, value) => {
        let errorText = "";
        if (key === "name") {
            errorText =  value.length > 0 ? "" : t('validations.invalid_name');
        }
        return errorText;
    }

    const activeCard = activeCampaignCard(campaign.settings);

    // Real usage_type cards, plus the synthetic Marketplace Buybox card in
    // wizard/add mode only. Switching an already-connected campaign's type from
    // the edit page must not be possible here -- clicking Buybox again would
    // reset settings.marketplace_api to {} and silently wipe its marketplace
    // connection. (Leaving marketplace mode via one of the four real cards is
    // still allowed in both modes -- see selectUsageType above.)
    // Card order: Competitive, Brand Protection, Marketplace Buybox, Marketplace
    // Operator, Market Research, Drop Shipping -- the two marketplace cards are
    // spliced in at positions 3-4 (between
    // the two "track and reprice" cards and the two research/fulfillment
    // ones), not appended at the end. usage_types(t) itself keeps its own
    // [seller, manufacturer, market_search, drop_shipping] order since it is
    // also consumed elsewhere as the real usage_type enum (see comment above).
    const baseCardOptions = usage_types(t);
    const cardOptions = isWizard
        ? [...baseCardOptions.slice(0, 2),
           {value: MARKETPLACE_BUYBOX, label: t('campaign.usage_type_marketplace_buybox')},
           {value: MARKETPLACE_OPERATOR, label: t('campaign.usage_type_marketplace_operator')},
           ...baseCardOptions.slice(2)]
        : baseCardOptions;

    return (
        <React.Fragment>
            {!isWizard && <h3>{props.page === "add" ? t('campaign.add_campaign') : t('campaign.edit_campaign')}</h3>}

            <div className="p-grid alignCenter p-fluid">
                <div className="p-col-12">
                    <span className="p-float-label">
                        <InputText id="name" name="name" value={campaign.name} onChange={onChange} className={errorClass("name")} autoFocus={true}/>
                        <label htmlFor="name">{t('campaign.name')}</label>
                    </span>
                    {errorClass("name") && (<Message severity="error" text={formErrors.name}/>)}
                </div>

                {/* Campaign type: selectable cards, each with a description. Always
                    rendered (including in wizard mode) -- Marketplace Buybox is just
                    a fifth card here, not a separate flow. */}
                <div className="p-col-12">
                    <div style={{fontWeight: 600, color: '#374151', margin: '4px 0 10px'}}>{t('campaign.usage_type')}</div>
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12}}>
                        {cardOptions.map(opt => {
                            const isBuybox = opt.value === MARKETPLACE_BUYBOX;
                            const isOperator = opt.value === MARKETPLACE_OPERATOR;
                            const meta = USAGE_TYPE_META[opt.value] || {icon: 'pi pi-tag', color: '#2563eb'};
                            // Buybox and Operator cards are active on their marker; the
                            // other four must NOT show active while a marker is present,
                            // since usage_type stays 'seller' for all of Competitive,
                            // Buybox and Operator (see activeCampaignCard).
                            const cardId = isBuybox ? CARD_BUYBOX : opt.value;
                            const active = activeCard === cardId;
                            const onSelect = () => isBuybox ? selectMarketplaceBuybox()
                                : isOperator ? selectMarketplaceOperator() : selectUsageType(opt.value);
                            return (
                                <div key={opt.value} role="button" tabIndex={0}
                                     onClick={onSelect}
                                     onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
                                     style={{position: 'relative', cursor: 'pointer', textAlign: 'left',
                                         border: `2px solid ${active ? meta.color : '#e5e7eb'}`,
                                         background: active ? `${meta.color}0d` : '#fff',
                                         borderRadius: 12, padding: 14,
                                         boxShadow: active ? `0 2px 10px ${meta.color}22` : '0 1px 2px rgba(0,0,0,.04)',
                                         transition: 'border-color .12s, box-shadow .12s, background .12s'}}>
                                    {active &&
                                        <i className="pi pi-check-circle"
                                           style={{position: 'absolute', top: 10, right: 10, color: meta.color, fontSize: '1.1rem'}}/>}
                                    <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, paddingRight: 20}}>
                                        <span style={{width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                            background: `${meta.color}14`, color: meta.color,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                            <i className={meta.icon} style={{fontSize: '1.2rem'}}/>
                                        </span>
                                        <span style={{fontWeight: 700, color: '#111827'}}>{opt.label}</span>
                                    </div>
                                    <div style={{color: '#6b7280', fontSize: '.82rem', lineHeight: 1.45}}>
                                        {t(`wizard.usageDesc.${opt.value}`)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {isAdmin() && props.page === "edit" &&
                    <div className="p-col-12 p-xl-12" style={{marginTop: 8}}>
                        <span className="p-float-label">
                            <Checkbox inputId="is_parent" name="is_parent" value={{}} checked={campaign.is_parent} onChange={onChange}/>
                            <label htmlFor="is_parent" style={{marginLeft:"1em", marginRight:"1em"}}>{t('campaign.is_parent_campaign')}</label>
                        </span>
                    </div>
                }
                {isAdmin() && props.page === "edit" &&
                    <div className="p-col-12 p-xl-12">
                        <span className="p-float-label">
                            <Dropdown showClear className="p-mr-1" id="parent_campaign" name="parent_campaign"
                                      value={campaign.parent_campaign}
                                      options={props.campaigns ? props.campaigns.filter(c => c.is_parent && c.id !== selectedCampaign?.id) : []}
                                      onChange={onChange} optionLabel="name"/>
                            <label htmlFor="parent_campaign">{t('campaign.parent_campaign')}</label>
                        </span>
                    </div>
                }
                {user().settings?.allow_currency_entry && <div className="p-col-12 p-xl-12">
                    <span className="p-float-label">
                        <InputText id="currency_rate" name="settings.currency_rate" value={campaign.settings?.currency_rate} onChange={onChange} />
                        <label htmlFor="currency_rate">{t('campaign.currency_rate')}</label>
                    </span>
                </div>}

                {!isWizard &&
                    <div className="p-col-6" style={{marginTop: 8}}>
                        <Button label={t('actions.cancel')} icon="pi pi-angle-double-left" className="p-button-outlined p-button-warning" onClick={() => props.returnPageList()}/>
                    </div>}
                {!isWizard &&
                    <div className="p-col-6" style={{marginTop: 8}}>
                        <Button label={t('actions.save')} icon="pi pi-save" className="p-button-outlined p-button-success" onClick={save}/>
                    </div>}
            </div>
        </React.Fragment>
    );
};
export default CampaignEdit
